'use client';

import type { Dispatch, ReactNode, SetStateAction } from 'react';
import {
  createElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  formatCategoryForDisplay,
  formatFindingStatusForDisplay,
  type EditableFields,
} from '@terra/ui-design-system';
import type { FindingStatusType } from '@terra/shared-schemas-orchestrator';
import { useFindingEditStore } from '../store/findingEditStore';
import type { FindingEditableField } from '../store/findingEditStore';
import { useCopilotFindingEditProcessingStore } from '../store/copilotFindingEditProcessingStore';
import { useChatInstancesStore } from '../store/chatInstancesStore';
import { usePocBlocksSuggestionLabels } from './usePocBlocksSuggestionLabels';
import {
  getNextPendingReviewField,
  getPendingFieldEdit,
  mergeReviewSessionFields,
  scrollToPendingFieldAfterLayout,
} from '../utils/findingPendingEditsUtils';
import {
  applyPendingSuggestionsToEditableFields,
  areFindingSuggestionValuesEqual,
  FINDING_SUGGESTION_CHROME_KIND,
  formatFindingSuggestionValue,
  getFindingSuggestionChromeKind,
  getLinkedFindingSuggestionFields,
  isFindingSuggestionAtBaseline,
  resolveDiscardedFindingSuggestionValue,
} from '../utils/findingSuggestionFieldUtils';
import { FindingPendingEditsBanner } from '../components/object-context/FindingPendingEditsBanner';
import type { FindingEditMarkableFieldProps } from '../components/tabs/findingEditForm.types';

type GetFindingEditState = ReturnType<
  typeof useFindingEditStore.getState
>['getFindingEditState'];

type UseFindingCopilotSuggestionReviewProps = {
  findingId: string | undefined;
  editableFields: EditableFields;
  setEditableFields: Dispatch<SetStateAction<EditableFields>>;
  originalFields: EditableFields | null | undefined;
  setMode: (mode: 'edit' | 'view') => void;
  setHasUnsavedChanges: Dispatch<SetStateAction<boolean>>;
  getFindingEditState: GetFindingEditState;
};

type UseFindingCopilotSuggestionReviewResult = {
  isCopilotProcessing: boolean;
  hasPendingSuggestions: boolean;
  suggestionReviewBar: ReactNode;
  getMarkableFieldProps: (
    field: FindingEditableField,
  ) => FindingEditMarkableFieldProps;
  handleStopCopilotWorking: () => Promise<void>;
  clearCopilotSuggestionsForFinding: (options?: {
    revertStagedFields?: boolean;
  }) => void;
  clearReviewSession: () => void;
  clearReviewFocus: () => void;
  discardPendingSelectSuggestionOnManualChange: <
    T extends keyof EditableFields,
  >(
    field: T,
    value: EditableFields[T],
  ) => void;
};

export function useFindingCopilotSuggestionReview({
  findingId,
  editableFields,
  setEditableFields,
  originalFields,
  setMode,
  setHasUnsavedChanges,
  getFindingEditState,
}: UseFindingCopilotSuggestionReviewProps): UseFindingCopilotSuggestionReviewResult {
  const pocSuggestionLabels = usePocBlocksSuggestionLabels();
  const acceptAiEdits = useFindingEditStore((state) => state.acceptAiEdits);
  const discardAiEdits = useFindingEditStore((state) => state.discardAiEdits);
  const updateEffect = useFindingEditStore((state) =>
    findingId ? state.updateEffects[findingId] : undefined,
  );
  const pendingAiEdits = useFindingEditStore((state) =>
    findingId ? state.pendingAiEdits[findingId] : undefined,
  );
  const isCopilotProcessing = useCopilotFindingEditProcessingStore(
    (state) => findingId != null && state.processingFindingId === findingId,
  );

  const previousPendingCountRef = useRef(0);
  const [reviewSessionFields, setReviewSessionFields] = useState<
    FindingEditableField[]
  >(() => pendingAiEdits?.fields.map((entry) => entry.field) ?? []);
  const reviewSessionFieldsRef = useRef(reviewSessionFields);
  const reviewSessionFindingIdRef = useRef(findingId);
  const [reviewFocusEffect, setReviewFocusEffect] = useState<{
    fields: FindingEditableField[];
    timestamp: number;
  }>();
  const [acceptedFlashField, setAcceptedFlashField] = useState<{
    field: FindingEditableField;
    timestamp: number;
  }>();

  reviewSessionFieldsRef.current = reviewSessionFields;

  useEffect(() => {
    if (!findingId || !updateEffect) {
      return;
    }

    const editState = getFindingEditState(findingId);

    if (!editState) {
      return;
    }

    setEditableFields((previousFields) => ({
      ...previousFields,
      ...(Object.fromEntries(
        updateEffect.fields.map((field) => [
          field,
          editState.editableFields[field],
        ]),
      ) as Partial<EditableFields>),
    }));
    setMode('edit');
    setHasUnsavedChanges(editState.hasUnsavedChanges);
  }, [
    findingId,
    getFindingEditState,
    setEditableFields,
    setHasUnsavedChanges,
    setMode,
    updateEffect,
  ]);

  useEffect(() => {
    if (reviewSessionFindingIdRef.current === findingId) {
      return;
    }

    reviewSessionFindingIdRef.current = findingId;
    setReviewSessionFields([]);
    setReviewFocusEffect(undefined);
    previousPendingCountRef.current = 0;
  }, [findingId]);

  useEffect(() => {
    const pendingFields =
      pendingAiEdits?.fields.map((entry) => entry.field) ?? [];
    const pendingCount = pendingFields.length;
    const firstPendingField = pendingFields[0];
    const hadPendingReview = previousPendingCountRef.current > 0;
    const isNewReview = !hadPendingReview;

    if (pendingCount === 0) {
      if (hadPendingReview) {
        setReviewSessionFields([]);
        setReviewFocusEffect(undefined);
      }

      previousPendingCountRef.current = 0;

      return;
    }

    setReviewSessionFields((sessionFields) => {
      const merged = mergeReviewSessionFields({
        sessionFields,
        pendingFields,
        isNewReview,
      });

      reviewSessionFieldsRef.current = merged;

      return merged;
    });

    setMode('edit');

    if (isCopilotProcessing) {
      previousPendingCountRef.current = pendingCount;

      return;
    }

    const focusedField = reviewFocusEffect?.fields[0];
    const focusedStillPending =
      focusedField !== undefined && pendingFields.includes(focusedField);
    const focusedInSession =
      focusedField !== undefined &&
      reviewSessionFieldsRef.current.includes(focusedField);

    if (!focusedStillPending && !focusedInSession && firstPendingField) {
      setReviewFocusEffect({
        fields: [firstPendingField],
        timestamp: Date.now(),
      });
    }

    previousPendingCountRef.current = pendingCount;
  }, [
    isCopilotProcessing,
    pendingAiEdits,
    pendingAiEdits?.fields.length,
    reviewFocusEffect,
    setMode,
  ]);

  useEffect(() => {
    const focusedField = reviewFocusEffect?.fields[0];

    if (!focusedField || isCopilotProcessing) {
      return;
    }

    return scrollToPendingFieldAfterLayout(focusedField);
  }, [
    isCopilotProcessing,
    reviewFocusEffect?.fields,
    reviewFocusEffect?.timestamp,
  ]);

  useEffect(() => {
    if (!pendingAiEdits?.fields.length) {
      return;
    }

    setEditableFields((previousFields) =>
      applyPendingSuggestionsToEditableFields({
        fields: previousFields,
        pendingFields: pendingAiEdits.fields,
        originalFields: originalFields ?? null,
        holdPendingSuggestions: isCopilotProcessing,
      }),
    );
  }, [isCopilotProcessing, originalFields, pendingAiEdits, setEditableFields]);

  useEffect(() => {
    if (!acceptedFlashField) {
      return;
    }

    const timeout = window.setTimeout(() => {
      setAcceptedFlashField((current) =>
        current?.timestamp === acceptedFlashField.timestamp
          ? undefined
          : current,
      );
    }, 1600);

    return () => window.clearTimeout(timeout);
  }, [acceptedFlashField]);

  const advanceReviewAfterFieldResolved = useCallback(
    (field: FindingEditableField) => {
      if (!findingId) {
        setReviewFocusEffect(undefined);

        return;
      }

      const remainingPending = useFindingEditStore
        .getState()
        .getPendingAiEdits(findingId);
      const nextField = getNextPendingReviewField({
        pendingAiEdits: remainingPending,
        currentField: field,
        pendingBeforeResolve: pendingAiEdits,
      });

      if (!nextField) {
        setReviewFocusEffect(undefined);

        return;
      }

      setReviewFocusEffect({
        fields: [nextField],
        timestamp: Date.now(),
      });
    },
    [findingId, pendingAiEdits],
  );

  const clearCopilotSuggestionsForFinding = useCallback(
    ({ revertStagedFields = false }: { revertStagedFields?: boolean } = {}) => {
      if (!findingId) {
        return;
      }

      const pending =
        useFindingEditStore.getState().getPendingAiEdits(findingId)?.fields ??
        [];

      discardAiEdits(findingId);
      setReviewSessionFields([]);

      if (!revertStagedFields || pending.length === 0) {
        return;
      }

      setEditableFields((previousFields) => {
        const nextFields = { ...previousFields };

        for (const entry of pending) {
          Object.assign(nextFields, {
            [entry.field]: resolveDiscardedFindingSuggestionValue({
              before: entry.before,
              original: originalFields?.[entry.field],
            }),
          });
        }

        return nextFields;
      });
    },
    [discardAiEdits, findingId, originalFields, setEditableFields],
  );

  const handleStopCopilotWorking = useCallback(async () => {
    if (!findingId) {
      return;
    }

    const { sourceChatId, suppressProcessingAfterStop } =
      useCopilotFindingEditProcessingStore.getState();
    const abortStream = sourceChatId
      ? useChatInstancesStore.getState().configs.get(sourceChatId)?.abortStream
      : undefined;

    if (sourceChatId) {
      suppressProcessingAfterStop(sourceChatId);
    }

    if (abortStream) {
      await abortStream();
    }

    clearCopilotSuggestionsForFinding({ revertStagedFields: true });
    setReviewFocusEffect(undefined);
  }, [clearCopilotSuggestionsForFinding, findingId]);

  const handleAcceptPendingField = useCallback(
    (field: FindingEditableField) => {
      if (!findingId) {
        return;
      }

      const pendingFieldKeys =
        pendingAiEdits?.fields.map((entry) => entry.field) ?? [];
      const fieldsToAccept = getLinkedFindingSuggestionFields({
        field,
        pendingFields: pendingFieldKeys,
      });

      if (fieldsToAccept.length === 0) {
        return;
      }

      acceptAiEdits(findingId, fieldsToAccept, {
        baseEditableFields: editableFields,
        originalFields: originalFields ?? null,
      });
      const nextFields =
        getFindingEditState(findingId)?.editableFields ?? editableFields;

      setEditableFields(nextFields);
      setAcceptedFlashField({ field, timestamp: Date.now() });

      const remainingPending = useFindingEditStore
        .getState()
        .getPendingAiEdits(findingId);
      const nextField = getNextPendingReviewField({
        pendingAiEdits: remainingPending,
        currentField: field,
        pendingBeforeResolve: pendingAiEdits,
      });

      if (!nextField) {
        setReviewFocusEffect(undefined);

        return;
      }

      setReviewFocusEffect({
        fields: [nextField],
        timestamp: Date.now(),
      });
    },
    [
      acceptAiEdits,
      editableFields,
      findingId,
      getFindingEditState,
      originalFields,
      pendingAiEdits,
      setEditableFields,
    ],
  );

  const handleDiscardPendingField = useCallback(
    (field: FindingEditableField) => {
      if (!findingId) {
        return;
      }

      const pendingFieldKeys =
        pendingAiEdits?.fields.map((entry) => entry.field) ?? [];
      const fieldsToDiscard = getLinkedFindingSuggestionFields({
        field,
        pendingFields: pendingFieldKeys,
      });

      if (fieldsToDiscard.length === 0) {
        return;
      }

      const pendingEntries =
        pendingAiEdits?.fields.filter((entry) =>
          fieldsToDiscard.includes(entry.field),
        ) ?? [];

      discardAiEdits(findingId, fieldsToDiscard);

      setEditableFields((previousFields) => {
        const nextFields = { ...previousFields };

        for (const entry of pendingEntries) {
          Object.assign(nextFields, {
            [entry.field]: resolveDiscardedFindingSuggestionValue({
              before: entry.before,
              original: originalFields?.[entry.field],
            }),
          });
        }

        return nextFields;
      });
      advanceReviewAfterFieldResolved(field);
    },
    [
      advanceReviewAfterFieldResolved,
      discardAiEdits,
      findingId,
      originalFields,
      pendingAiEdits,
      setEditableFields,
    ],
  );

  const handleFocusPendingField = useCallback((field: FindingEditableField) => {
    setReviewFocusEffect({
      fields: [field],
      timestamp: Date.now(),
    });
  }, []);

  const formatPendingFieldValue = useCallback(
    (field: FindingEditableField, value: unknown) => {
      if (field === 'status' && typeof value === 'string') {
        return formatFindingStatusForDisplay(value as FindingStatusType);
      }

      if (field === 'category' && typeof value === 'string') {
        return formatCategoryForDisplay(value);
      }

      return formatFindingSuggestionValue({
        value,
        labels: pocSuggestionLabels,
      });
    },
    [pocSuggestionLabels],
  );

  const getMarkableFieldProps = useCallback(
    (field: FindingEditableField): FindingEditMarkableFieldProps => {
      const pendingEdit = isCopilotProcessing
        ? undefined
        : getPendingFieldEdit({ pendingAiEdits, field });
      const isAcceptedFlash =
        !isCopilotProcessing && acceptedFlashField?.field === field;
      const isReviewing = Boolean(
        !isCopilotProcessing &&
          !isAcceptedFlash &&
          reviewFocusEffect?.fields.includes(field),
      );

      return {
        updateEffect:
          isCopilotProcessing || isAcceptedFlash ? undefined : updateEffect,
        pendingEdit,
        isReviewing,
        reviewArrivalAt: isReviewing ? reviewFocusEffect?.timestamp : undefined,
        isAcceptedFlash,
        currentValue: editableFields[field],
        baselineValue: originalFields?.[field],
        formatPendingValue: (value: unknown) =>
          formatPendingFieldValue(field, value),
        onAcceptPendingEdit: pendingEdit
          ? () => handleAcceptPendingField(field)
          : undefined,
        onDiscardPendingEdit: pendingEdit
          ? () => handleDiscardPendingField(field)
          : undefined,
        onActivatePendingTextEdit:
          pendingEdit &&
          getFindingSuggestionChromeKind(pendingEdit.field) ===
            FINDING_SUGGESTION_CHROME_KIND.text
            ? () => {
                if (
                  !isFindingSuggestionAtBaseline({
                    current: editableFields[field],
                    before: pendingEdit.before,
                    original: originalFields?.[field],
                  })
                ) {
                  return;
                }

                setEditableFields((previousFields) => ({
                  ...previousFields,
                  [field]: pendingEdit.after as EditableFields[typeof field],
                }));
              }
            : undefined,
      };
    },
    [
      acceptedFlashField,
      editableFields,
      formatPendingFieldValue,
      handleAcceptPendingField,
      handleDiscardPendingField,
      isCopilotProcessing,
      originalFields,
      pendingAiEdits,
      reviewFocusEffect,
      setEditableFields,
      updateEffect,
    ],
  );

  const clearReviewSession = useCallback(() => {
    setReviewSessionFields([]);
  }, []);

  const clearReviewFocus = useCallback(() => {
    setReviewFocusEffect(undefined);
  }, []);

  const discardPendingSelectSuggestionOnManualChange = useCallback(
    <T extends keyof EditableFields>(field: T, value: EditableFields[T]) => {
      const pendingEdit = getPendingFieldEdit({
        pendingAiEdits,
        field: field as FindingEditableField,
      });

      if (
        findingId &&
        pendingEdit &&
        getFindingSuggestionChromeKind(pendingEdit.field) ===
          FINDING_SUGGESTION_CHROME_KIND.select &&
        !areFindingSuggestionValuesEqual({
          left: value,
          right: pendingEdit.after,
        })
      ) {
        discardAiEdits(findingId, [pendingEdit.field]);
        advanceReviewAfterFieldResolved(pendingEdit.field);
      }
    },
    [
      advanceReviewAfterFieldResolved,
      discardAiEdits,
      findingId,
      pendingAiEdits,
    ],
  );

  const hasPendingSuggestions = (pendingAiEdits?.fields.length ?? 0) > 0;
  const focusedReviewField =
    reviewFocusEffect?.fields[0] ??
    (hasPendingSuggestions ? reviewSessionFields[0] : undefined);
  const focusedPendingEdit = focusedReviewField
    ? getPendingFieldEdit({ pendingAiEdits, field: focusedReviewField })
    : undefined;
  const suggestionReviewBar = useMemo<ReactNode>(() => {
    if (!hasPendingSuggestions || reviewSessionFields.length === 0) {
      return null;
    }

    return createElement(FindingPendingEditsBanner, {
      sessionFields: reviewSessionFields,
      pendingCount: pendingAiEdits?.fields.length ?? 0,
      focusedField: focusedReviewField,
      onFocusField: handleFocusPendingField,
      ...(focusedReviewField && focusedPendingEdit
        ? {
            focusedActions: {
              onAccept: () => handleAcceptPendingField(focusedReviewField),
              onDiscard: () => handleDiscardPendingField(focusedReviewField),
            },
          }
        : {}),
    });
  }, [
    focusedPendingEdit,
    focusedReviewField,
    handleAcceptPendingField,
    handleDiscardPendingField,
    handleFocusPendingField,
    hasPendingSuggestions,
    pendingAiEdits,
    reviewSessionFields,
  ]);

  return {
    isCopilotProcessing,
    hasPendingSuggestions,
    suggestionReviewBar,
    getMarkableFieldProps,
    handleStopCopilotWorking,
    clearCopilotSuggestionsForFinding,
    clearReviewSession,
    clearReviewFocus,
    discardPendingSelectSuggestionOnManualChange,
  };
}
